import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCompleteInvite, useVerifyInvite } from '../../store/queryHooks';
import styles from './SetPasswordPage.module.css';

type Status =
  | { kind: 'loading' }
  | { kind: 'ready'; name: string; email: string }
  | { kind: 'invalid'; message: string };

export function SetPasswordPage() {
  const [search] = useSearchParams();
  const token = search.get('konto') ?? '';
  const navigate = useNavigate();
  const verify = useVerifyInvite();
  const complete = useCompleteInvite();
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const [pw, setPw] = useState('');
  const [repeat, setRepeat] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setStatus({ kind: 'invalid', message: 'Ingen konto-nøgle angivet i linket.' });
      return;
    }
    verify
      .mutateAsync(token)
      .then((res) => {
        if (cancelled || !res) return;
        setStatus({ kind: 'ready', name: res.name, email: res.email });
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Linket er ugyldigt eller allerede brugt';
        setStatus({ kind: 'invalid', message: msg });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (pw.length < 6) {
      setFormError('Adgangskoden skal være mindst 6 tegn');
      return;
    }
    if (pw !== repeat) {
      setFormError('Adgangskoderne er ikke ens');
      return;
    }
    setSubmitting(true);
    try {
      await complete.mutateAsync({ token, password: pw });
      // Server set the session cookie — go straight to Overblik.
      navigate('/overblik', { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Kunne ikke oprette adgangskoden';
      setFormError(msg);
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.wrap}>
        <div className={styles.brand}>
          <div className={styles.brandBadge}>B</div>
          <div className={styles.brandName}>BudgetX</div>
        </div>
        <div className={styles.card}>
          {status.kind === 'loading' && (
            <>
              <h1 className={styles.title}>Et øjeblik…</h1>
              <p className={styles.sub}>Tjekker dit invitationslink.</p>
            </>
          )}
          {status.kind === 'invalid' && (
            <>
              <h1 className={styles.title}>Linket virker ikke</h1>
              <p className={styles.sub}>{status.message}</p>
              <p className={styles.sub}>
                Kontakt din administrator og bed om et nyt invitationslink.
              </p>
            </>
          )}
          {status.kind === 'ready' && (
            <form onSubmit={onSubmit}>
              <h1 className={styles.title}>Vælg din adgangskode</h1>
              <p className={styles.sub}>
                Velkommen, {status.name}. Vælg en adgangskode, så du kan logge ind på jeres budget.
              </p>
              <div className={styles.form}>
                <div>
                  <label className={styles.label} htmlFor="new-pw">
                    Adgangskode
                  </label>
                  <input
                    id="new-pw"
                    type="password"
                    className={styles.input}
                    placeholder="Mindst 6 tegn"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    autoFocus
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <label className={styles.label} htmlFor="repeat-pw">
                    Gentag adgangskode
                  </label>
                  <input
                    id="repeat-pw"
                    type="password"
                    className={styles.input}
                    placeholder="Gentag"
                    value={repeat}
                    onChange={(e) => setRepeat(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
              </div>
              {formError && <div className={styles.error}>{formError}</div>}
              <button type="submit" className={styles.primaryBtn} disabled={submitting}>
                {submitting ? 'Opretter…' : 'Opret adgangskode'}
              </button>
              <div className={styles.emailLine}>Logges ind som {status.email}</div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
